import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trash, ArrowCounterClockwise } from '@phosphor-icons/react';
import { useApp } from '../../context/AppContext';

// App-level floating undo banner for object deletes.
//
// Surfaces from the bottom-center for ~12s after any object is soft-deleted
// (whether from the Objects page, the storyboard drawer, or anywhere else
// that calls AppContext.deleteObject). Click Undo and AppContext.restoreObject
// hits the backend's /:id/restore route, which clears the soft-delete
// timestamp set by the matching DELETE call. Past 12s the banner
// disappears, but the row's TTL is 48h so a future "recently deleted"
// view could still surface a manual restore.
//
// Mounted once in MainLayout so it's available on every page that can
// trigger an object delete.
const ObjectUndoBanner = () => {
    const { recentlyDeletedObjects, restoreObject } = useApp();
    const list = recentlyDeletedObjects || [];
    const latest = list[list.length - 1];

    return (
        <AnimatePresence>
            {latest && (
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 16 }}
                    transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                    // Sit above ToastContainer's bottom-corner toasts so the
                    // banner doesn't get stacked on top of unrelated messages.
                    className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-3 px-4 py-2.5 rounded-full bg-[rgba(10,12,22,0.96)] backdrop-blur-2xl border border-white/15 shadow-[0_12px_40px_rgba(0,0,0,0.6)]"
                >
                    <Trash size={14} className="text-white/60" />
                    <span className="text-[12px] text-white/85 font-semibold">
                        {list.length === 1
                            ? <>"{latest.name}" deleted</>
                            : <>{list.length} objects deleted</>}
                    </span>
                    <button
                        onClick={() => {
                            // Restore in reverse order so the most recent
                            // delete comes back first — matches the user's
                            // mental model of "Undo my last action".
                            for (let i = list.length - 1; i >= 0; i--) {
                                restoreObject(list[i].id);
                            }
                        }}
                        className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-cyan-500 hover:bg-cyan-400 text-black text-[10px] font-bold uppercase tracking-widest transition-all shadow-[0_0_12px_rgba(24,181,240,0.4)]"
                    >
                        <ArrowCounterClockwise size={11} weight="bold" />
                        Undo
                    </button>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default ObjectUndoBanner;
